import type { NAnalytics, NFiles } from '@app/types';

import type { WithActiveBranchId } from '../shared';

export enum AnalyticsEndpoints {
  GetAnalyticsFilterData = 'GetAnalyticsFilterData',
  GetAnalyticsWidget = 'GetAnalyticsWidget',
  GetAnalyticsServiceCategories = 'GetAnalyticsServiceCategories',
  GetFinancialStatistics = 'GetFinancialStatistics',
  GetFinancialStatisticsBySearchId = 'GetFinancialStatisticsBySearchId',
  GetOrganizationClientsGrowth = 'GetOrganizationClientsGrowth',
  GetBranchIndicators = 'GetBranchIndicators',
  GetPnLReport = 'GetPnLReport',
  GetIncomesReport = 'GetIncomesReport',
  GetFactImplReport = 'GetFactImplReport',
  GetExpenseByArticlesReport = 'GetExpenseByArticlesReport',
  GetDetailsOfExpensesReport = 'GetDetailsOfExpensesReport',
  ExportToExcelFinancialPnLReport = 'ExportToExcelFinancialPnLReport',
  ExportToExcelFinancialIncomesReport = 'ExportToExcelFinancialIncomesReport',
  ExportToExcelFinancialFactImplReport = 'ExportToExcelFinancialFactImplReport',
  ExportToExcelFinancialExpensesByArticlesReport = 'ExportToExcelFinancialExpensesByArticlesReport',
  ExportToExcelFinancialDetailsOfExpensesReport = 'ExportToExcelFinancialDetailsOfExpensesReport',
  ExportToExcelFinancialReports = 'ExportToExcelFinancialReports',
}

export interface AnalyticsFinancialPeriodRequest extends WithActiveBranchId {
  filterMonth: number;
  filterYear: number;
}

export type GetAnalyticsFilterDataRequest = WithActiveBranchId;
export type GetAnalyticsWidgetRequest = WithActiveBranchId<{
  data: NAnalytics.ModelAnalyticsFilter;
}>;
export type GetAnalyticsServiceCategoriesRequest = WithActiveBranchId<{
  data: NAnalytics.ModelAnalyticsServiceCategoriesFilter;
}>;
export type GetFinancialStatisticsRequest = WithActiveBranchId<{
  dateS: string;
  dateE: string;
}>;
export type GetFinancialStatisticsBySearchIdRequest = WithActiveBranchId<{
  searchId: number | null;
}>;
export type GetOrganizationClientsGrowthRequest = WithActiveBranchId<{
  data: NAnalytics.ModelClientsGrowthFilter;
}>;

export interface ModelBranchIndicatorsReportFilter {
  Year: number;
  Month: number;
  BranchIds: number[] | null;
}

export interface ModelAnalyticsFinancialFilter {
  From: string;
  To: string;
}

export interface ModelBranchIndicatorField {
  Value: number;
  DiffByMonth: number;
  DiffByYear: number;
}

export interface ModelBranchIndicatorsTotal {
  Incomes: ModelBranchIndicatorField;
  FactImpl: ModelBranchIndicatorField;
  Plan: ModelBranchIndicatorField;
  ExecutionPlan: ModelBranchIndicatorField;
  Expense: ModelBranchIndicatorField;
  Profit: ModelBranchIndicatorField;
}

export interface ModelBranchIndicatorsRow extends ModelBranchIndicatorsTotal {
  BranchId: number;
  BranchName: string;
}

export interface ModelBranchIndicatorsReport {
  Filter: ModelBranchIndicatorsReportFilter;
  Rows: ModelBranchIndicatorsRow[] | null;
  Totals: ModelBranchIndicatorsTotal;
}

export type GetBranchIndicatorsRequest = WithActiveBranchId<{
  data: ModelBranchIndicatorsReportFilter;
}>;

export interface ModelFinancialPnLRow {
  Label: string;
  Amount: number;
  PercentageIncome: number;
  PercentageGrowthPrevMonth: number;
  PercentageGrowthPrevYear: number;
}

export interface ModelFinancialPnLExpenseRow extends ModelFinancialPnLRow {
  ArticleId: number;
}

export interface ModelFinancialPnlResult {
  Filter: ModelAnalyticsFinancialFilter;
  FactImpl: ModelFinancialPnLRow;
  ExpenseTotal: ModelFinancialPnLRow;
  ExpensesByArticles: ModelFinancialPnLExpenseRow[] | null;
  Profit: ModelFinancialPnLRow;
}

export interface ModelPaymentIncomeItem {
  Income: number;
  PaymentAccount: string;
}

export interface ModelIncomeBaseRow {
  Cash: ModelPaymentIncomeItem[];
  Cashless: ModelPaymentIncomeItem[];
  Total: number;
}

export interface ModelIncomeRow extends ModelIncomeBaseRow {
  Date: string;
}

export interface ModelIncomesReport {
  Filter: ModelAnalyticsFinancialFilter;
  Rows: ModelIncomeRow[] | null;
  Totals: ModelIncomeBaseRow;
}

export interface ModelFactImplDayRow {
  AmountCash: number;
  AmountCard: number;
  AmountFromBalance: number;
  Total: number;
  Date: string;
}

export interface ModelFactImplRow {
  AmountCash: number;
  AmountCard: number;
  AmountFromBalance: number;
  Total: number;
}

export interface ModelFactImplReport {
  Filter: ModelAnalyticsFinancialFilter;
  DayRows: ModelFactImplDayRow[] | null;
  TotalRow: ModelFactImplRow;
}

export interface ModelExpenseByCommentRow {
  Comment: string;
  Amount: number;
  Timestamp: string;
}

export interface ModelExpenseByArticleRow {
  ArticleId: number;
  ExpensesByComments: ModelExpenseByCommentRow[] | null;
  Amount: number;
}

export interface ModelExpenseByArticlesReport {
  Filter: ModelAnalyticsFinancialFilter;
  ExpensesByArticles: ModelExpenseByArticleRow[] | null;
  ArticleWithNames: Record<string, string> | null;
}

export interface ModelExpenseOfDetail {
  Id: number;
  Amount: number;
  DateTime: string;
  Comment: string;
  AccountId: number;
  ArticleId: number;
  HaveFile: boolean;
}

export interface ModelExpenseOfDetailBase {
  AccountId: number;
  Amount: number;
}

export interface ModelExpenseByDay {
  Date: string;
  Expenses: ModelExpenseOfDetail[] | null;
}

export interface ModelExpenseOfDetailsReport {
  Filter: ModelAnalyticsFinancialFilter;
  Days: ModelExpenseByDay[] | null;
  AccountWithNames: Record<string, string> | null;
  Totals: ModelExpenseOfDetailBase[] | null;
}

export type GetAnalyticsFilterDataResponse =
  NAnalytics.ModelAnalyticsFilterData;
export type GetAnalyticsWidgetResponse = NAnalytics.ModelAnalyticsWidget;
export type GetAnalyticsServiceCategoriesResponse =
  NAnalytics.ModelAnalyticsServiceCategories;
export type GetFinancialStatisticsResponse = NAnalytics.ModelFinancialStatistic;
export type GetFinancialStatisticsBySearchIdResponse =
  NAnalytics.ModelFinancialStatistic;
export type GetOrganizationClientsGrowthResponse =
  NAnalytics.ModelClientsGrowthResult;
export type GetBranchIndicatorsResponse = ModelBranchIndicatorsReport;
export type GetPnLReportResponse = ModelFinancialPnlResult;
export type GetIncomesReportResponse = ModelIncomesReport;
export type GetFactImplReportResponse = ModelFactImplReport;
export type GetExpenseByArticlesReportResponse = ModelExpenseByArticlesReport;
export type GetDetailsOfExpensesReportResponse = ModelExpenseOfDetailsReport;
export type ExportToExcelFinancialPnLReportResponse = NFiles.ModelFile;
export type ExportToExcelFinancialIncomesReportResponse = NFiles.ModelFile;
export type ExportToExcelFinancialFactImplReportResponse = NFiles.ModelFile;
export type ExportToExcelFinancialExpensesByArticlesReportResponse =
  NFiles.ModelFile;
export type ExportToExcelFinancialDetailsOfExpensesReportResponse =
  NFiles.ModelFile;
export type ExportToExcelFinancialReportsResponse = NFiles.ModelFile;

export type GetPnLReportRequest = AnalyticsFinancialPeriodRequest;
export type GetIncomesReportRequest = AnalyticsFinancialPeriodRequest;
export type GetFactImplReportRequest = AnalyticsFinancialPeriodRequest;
export type GetExpenseByArticlesReportRequest = AnalyticsFinancialPeriodRequest;
export type GetDetailsOfExpensesReportRequest = AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialPnLReportRequest =
  AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialIncomesReportRequest =
  AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialFactImplReportRequest =
  AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialExpensesByArticlesReportRequest =
  AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialDetailsOfExpensesReportRequest =
  AnalyticsFinancialPeriodRequest;
export type ExportToExcelFinancialReportsRequest =
  AnalyticsFinancialPeriodRequest;
